(() => {
  const mq = window.matchMedia('(max-width: 749px)');
  const READY = 'folkartV6Ready';
  const START_THRESHOLD = 7;
  const SWIPE_FRACTION = 0.12;
  const MIN_SWIPE_PX = 42;
  const VELOCITY_THRESHOLD = 0.34;

  const getVisible = (gallery) =>
    Array.from(gallery.querySelectorAll('.product-gallery__media')).filter((item) => !item.hidden);

  const dims = (item) => {
    const width = Number(item.dataset.mediaWidth) || Number(item.querySelector('img')?.getAttribute('width')) || 0;
    const height = Number(item.dataset.mediaHeight) || Number(item.querySelector('img')?.getAttribute('height')) || 0;
    return width > 0 && height > 0 ? { width, height } : null;
  };

  const init = (gallery) => {
    if (gallery.dataset[READY] === 'true') return;
    const carousel = gallery.querySelector('.product-gallery__carousel');
    if (!carousel) return;
    gallery.dataset[READY] = 'true';

    let items = [];
    let current = 0;
    let gesture = null;
    let suppressClickUntil = 0;
    let stableHeight = window.innerHeight;
    let stableWidth = window.innerWidth;

    const maxHeight = () => 480;

    const refreshItems = () => {
      const previousId = items[current]?.dataset.mediaId;
      items = getVisible(gallery);
      if (!items.length) return;
      const same = previousId ? items.findIndex((item) => item.dataset.mediaId === previousId) : -1;
      if (same >= 0) current = same;
      else {
        const initial = items.findIndex((item) => item.classList.contains('is-initial'));
        current = initial >= 0 ? initial : 0;
      }
      current = Math.max(0, Math.min(current, items.length - 1));
    };

    const itemHeight = (item) => {
      if (!item) return 0;
      const size = dims(item);
      if (!size) return Math.min(item.getBoundingClientRect().height || carousel.clientWidth, maxHeight());
      return Math.min(carousel.clientWidth * size.height / size.width, maxHeight());
    };

    const setHeight = (height, animate = true) => {
      if (!height) return;
      carousel.style.transitionDuration = animate ? '140ms' : '0ms';
      carousel.style.setProperty('--folkart-gallery-height', `${Math.ceil(height)}px`);
      if (!animate) requestAnimationFrame(() => { carousel.style.transitionDuration = ''; });
    };

    const warm = (item) => {
      const image = item?.querySelector('img');
      if (!image) return;
      image.loading = 'eager';
      image.decoding = 'async';
      if (typeof image.decode === 'function') image.decode().catch(() => {});
    };

    const warmAround = (index) => {
      [index - 1, index, index + 1].forEach((i) => {
        if (i >= 0 && i < items.length) warm(items[i]);
      });
    };

    const updateThumbs = () => {
      const id = items[current]?.dataset.mediaId;
      gallery.querySelectorAll('.product-gallery__thumbnail').forEach((thumb) => {
        const active = thumb.dataset.mediaId === id;
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      });
    };

    const place = (dragPx = 0, animate = true) => {
      const width = carousel.clientWidth || window.innerWidth;
      carousel.dataset.folkartDragging = animate ? 'false' : 'true';

      items.forEach((item, index) => {
        const x = (index - current) * width + dragPx;
        item.style.transform = `translate3d(${Math.round(x)}px,0,0)`;
        const active = index === current && dragPx === 0;
        item.dataset.folkartActive = active ? 'true' : 'false';
        item.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    };

    const goTo = (next, animate = true) => {
      if (!items.length) return;
      current = Math.max(0, Math.min(next, items.length - 1));
      carousel.dataset.folkartDragging = 'false';
      setHeight(itemHeight(items[current]), animate);
      place(0, animate);
      updateThumbs();
      warmAround(current);
    };

    const resetStableViewport = () => {
      stableHeight = window.innerHeight;
      stableWidth = window.innerWidth;
      gallery.style.setProperty('--folkart-gallery-max-height', `${Math.round(maxHeight())}px`);
    };

    const deactivateMobileSlider = () => {
      gesture = null;
      carousel.style.removeProperty('--folkart-gallery-height');
      carousel.style.removeProperty('transition-duration');
      gallery.style.removeProperty('--folkart-gallery-max-height');
      delete carousel.dataset.folkartDragging;

      items.forEach((item) => {
        item.style.removeProperty('transform');
        item.removeAttribute('data-folkart-active');
        item.removeAttribute('aria-hidden');
      });
    };

    refreshItems();

    if (mq.matches) {
      resetStableViewport();
      goTo(current, false);
    } else {
      deactivateMobileSlider();
    }

    carousel.addEventListener('touchstart', (event) => {
      if (!mq.matches || event.touches.length !== 1 || !items.length) return;
      const touch = event.touches[0];
      gesture = {
        x: touch.clientX,
        y: touch.clientY,
        time: performance.now(),
        dx: 0,
        dy: 0,
        axis: null,
        moved: false
      };
    }, { passive: true });

    carousel.addEventListener('touchmove', (event) => {
      if (!mq.matches || !gesture || event.touches.length !== 1) return;
      const touch = event.touches[0];
      let dx = touch.clientX - gesture.x;
      const dy = touch.clientY - gesture.y;
      gesture.dx = dx;
      gesture.dy = dy;

      const ax = Math.abs(dx);
      const ay = Math.abs(dy);
      if (!gesture.axis && Math.max(ax, ay) >= START_THRESHOLD) {
        gesture.axis = ax > ay * 1.08 ? 'x' : 'y';
        gesture.moved = true;
      }

      if (gesture.axis === 'y') {
        suppressClickUntil = performance.now() + 450;
        return;
      }
      if (gesture.axis !== 'x') return;

      event.preventDefault();
      suppressClickUntil = performance.now() + 450;

      const width = carousel.clientWidth || window.innerWidth;
      const atStart = current === 0 && dx > 0;
      const atEnd = current === items.length - 1 && dx < 0;
      if (atStart || atEnd) dx *= 0.28;

      carousel.dataset.folkartDragging = 'true';
      place(dx, false);

      const direction = dx < 0 ? 1 : -1;
      const target = Math.max(0, Math.min(items.length - 1, current + direction));
      if (target !== current) {
        const progress = Math.min(1, Math.abs(dx) / width);
        const fromH = itemHeight(items[current]);
        const toH = itemHeight(items[target]);
        setHeight(fromH + (toH - fromH) * progress, false);
        warm(items[target]);
      }
    }, { passive: false });

    const finish = () => {
      if (!gesture) return;
      const g = gesture;
      gesture = null;

      if (g.axis !== 'x') {
        if (g.moved) suppressClickUntil = performance.now() + 450;
        goTo(current, true);
        return;
      }

      const elapsed = Math.max(1, performance.now() - g.time);
      const velocity = Math.abs(g.dx) / elapsed;
      const width = carousel.clientWidth || window.innerWidth;
      const threshold = Math.max(MIN_SWIPE_PX, width * SWIPE_FRACTION);
      const commit = Math.abs(g.dx) >= threshold || velocity >= VELOCITY_THRESHOLD;
      const direction = g.dx < 0 ? 1 : -1;
      goTo(commit ? current + direction : current, true);
      suppressClickUntil = performance.now() + 450;
    };

    carousel.addEventListener('touchend', finish, { passive: true });
    carousel.addEventListener('touchcancel', finish, { passive: true });

    carousel.addEventListener('click', (event) => {
      if (performance.now() < suppressClickUntil && event.target.closest('.product-gallery__media')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    const nav = gallery.querySelector('product-gallery-navigation');
    nav?.addEventListener('click', (event) => {
      if (!mq.matches) return;
      const thumb = event.target.closest('.product-gallery__thumbnail');
      if (!thumb) return;
      const index = items.findIndex((item) => item.dataset.mediaId === thumb.dataset.mediaId);
      if (index < 0) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      goTo(index, true);
    }, true);

    const mutationObserver = new MutationObserver(() => {
      const activeId = items[current]?.dataset.mediaId;
      const nextItems = getVisible(gallery);
      const changed = nextItems.length !== items.length || nextItems.some((item, i) => item !== items[i]);
      if (!changed) return;

      items = nextItems;
      const preservedIndex = items.findIndex((item) => item.dataset.mediaId === activeId);
      current = preservedIndex >= 0 ? preservedIndex : 0;

      if (!mq.matches) {
        deactivateMobileSlider();
        return;
      }

      goTo(current, false);
    });
    mutationObserver.observe(gallery, { subtree: true, attributes: true, attributeFilter: ['hidden'] });

    mq.addEventListener('change', () => {
      refreshItems();

      if (mq.matches) {
        resetStableViewport();
        goTo(current, false);
      } else {
        deactivateMobileSlider();
      }
    });

    window.addEventListener('orientationchange', () => {
      if (!mq.matches) return;

      setTimeout(() => {
        resetStableViewport();
        goTo(current, false);
      }, 180);
    }, { passive: true });

    window.addEventListener('resize', () => {
      if (!mq.matches) return;
      if (Math.abs(window.innerWidth - stableWidth) < 40) return;
      resetStableViewport();
      goTo(current, false);
    }, { passive: true });
  };

  const scan = (root = document) => {
    root.querySelectorAll?.('.shopify-section--main-product product-gallery').forEach(init);
    if (root.matches?.('.shopify-section--main-product product-gallery')) init(root);
  };

  const boot = () => {
    scan();
    const observer = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node);
      }));
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();